import { Box, Card, Chip, CircularProgress, Grid, Typography } from "@mui/joy";
import { useContext, useEffect } from "react";
import {
    ChatBubbleRounded,
    WarningRounded,
    AccessTimeRounded,
    TrendingUpRounded,
    TrendingDownRounded,
    MarkChatUnreadRounded,
    TextSnippetRounded,
} from '@mui/icons-material';
import { LayoutContext } from "../context";
import { formatResponseDuration } from "../components/MessagesChat/ChatBubble";


const StatCard = ({ title, value, icon, trend, subtitle, color = 'primary' }) => {
    const getTrendIcon = () => {
        if (trend > 0) return <TrendingUpRounded color="success" />;
        if (trend < 0) return <TrendingDownRounded color="danger" />;
        return null;
    };
    const getTrendColor = () => {
        if (trend > 0) return 'success';
        if (trend < 0) return 'danger';
        return 'neutral';
    };

    return (
        <Card variant="outlined" sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography level="h2" fontSize="xl" sx={{ mb: 0.5 }}>
                        {value ?? '—'}
                    </Typography>
                    <Typography level="body2" sx={{ mb: 1 }}>{title}</Typography>
                    {subtitle && (
                        <Typography level="body3" color="neutral">{subtitle}</Typography>
                    )}
                </Box>
                <Box sx={{
                    p: 1, borderRadius: '50%',
                    backgroundColor: `${color}.100`, color: `${color}.500`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    {icon}
                </Box>
            </Box>
            {trend !== undefined && trend !== 0 && (
                <Chip
                    variant="soft" size="sm" color={getTrendColor()}
                    startDecorator={getTrendIcon()}
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                >
                    {Math.abs(trend)}%
                </Chip>
            )}
        </Card>
    );
};


export default function ChatStatsPage() {
    const { chatStats, chatStatsLoading, loadChatStats } = useContext(LayoutContext);

    useEffect(() => {
        loadChatStats();
    }, []);

    if (chatStatsLoading || !chatStats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const {
        active_chats = 0,
        messages_today = 0,
        activity_trend = 0,
        total_danger_messages = 0,
        recent_danger_messages = 0,
        avg_doctor_response_seconds,
        avg_message_length = 0,
        my_unread_messages,
    } = chatStats;

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 1,
                }}
            >
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                    Статистика чатов
                </Typography>

                <Grid container spacing={1}>
                    <Grid xs={12} sm={6} md={3}>
                        <StatCard
                            title="Активные чаты"
                            value={active_chats}
                            icon={<ChatBubbleRounded />}
                            subtitle="За последние 30 дней"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                        <StatCard
                            title="Сообщений сегодня"
                            value={messages_today}
                            icon={<AccessTimeRounded />}
                            trend={activity_trend}
                            subtitle={activity_trend !== 0 ? `${activity_trend > 0 ? '+' : ''}${activity_trend}% к вчера` : 'без изменений'}
                            color="success"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                        <StatCard
                            title="Опасных сообщений"
                            value={total_danger_messages}
                            icon={<WarningRounded />}
                            subtitle={`${recent_danger_messages} за неделю`}
                            color="danger"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                        <StatCard
                            title="Среднее время ответа"
                            value={formatResponseDuration(avg_doctor_response_seconds) || '—'}
                            icon={<AccessTimeRounded />}
                            subtitle="Врач отвечает в среднем через"
                            color="primary"
                        />
                    </Grid>

                    <Grid xs={12} sm={6} md={6}>
                        <StatCard
                            title="Средняя длина сообщения"
                            value={`${Math.round(avg_message_length)} симв.`}
                            icon={<TextSnippetRounded />}
                            color="neutral"
                        />
                    </Grid>
                    {my_unread_messages != null && (
                        <Grid xs={12} sm={6} md={6}>
                            <StatCard
                                title="Мои непрочитанные"
                                value={my_unread_messages}
                                icon={<MarkChatUnreadRounded />}
                                color="warning"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    );
}
